import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Checkbox,
  FormControlLabel,
  Avatar,
  IconButton,
  Tooltip,
  LinearProgress,
  Tabs,
  Tab, MenuItem,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import LinkIcon from '@mui/icons-material/Link';
import DeleteIcon from '@mui/icons-material/Delete';
import { PersonalData } from '../types';
import { PersonalDataAPI } from '../services/API/PersonalDataAPI';
import { UtilityForm } from '../utils/UtilityForm';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { Log } from '../utils/Log';
import { useLocale } from '../contexts/LocaleContext';
import { useTranslation } from 'react-i18next';
import NationalityAutocomplete from '../components/NationalityAutocomplete';

// ─── ImgBB ────────────────────────────────────────────────────────────────────
// Inserisci la tua API key di ImgBB qui oppure leggila da una variabile d'ambiente.
// Esempio: const IMGBB_API_KEY = import.meta.env.VITE_IMGBB_API_KEY;
const IMGBB_API_KEY = process.env.REACT_APP_VITE_IMGBB_API_KEY || '';

interface ImgBBResponse {
  data: {
    url: string;
    display_url: string;
    delete_url: string;
    thumb: { url: string };
  };
  success: boolean;
  status: number;
}

async function uploadToImgBB(file: File): Promise<{ url: string; deleteUrl: string }> {
  console.log(process.env.REACT_APP_VITE_IMGBB_API_KEY);
  if (!IMGBB_API_KEY) {
    throw new Error('IMGBB_API_KEY non configurata.');
  }
  const formData = new FormData();
  formData.append('image', file);

  const response = await fetch(
      `https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}`,
      { method: 'POST', body: formData }
  );

  if (!response.ok) {
    throw new Error(`ImgBB upload fallito: ${response.statusText}`);
  }

  const json: ImgBBResponse = await response.json();
  if (!json.success) {
    throw new Error('ImgBB ha restituito un errore.');
  }

  return {
    url: json.data.display_url,
    deleteUrl: json.data.delete_url,
  };
}
// ──────────────────────────────────────────────────────────────────────────────

/** Scheda di selezione modalità upload */
type PhotoMode = 'url' | 'upload';

const PersonalDataPage: React.FC = () => {
  const [personalData, setPersonalData] = useState<PersonalData>({
    id: '',
    userId: '',
    name: '',
    surname: '',
    createdAt: '',
    updatedAt: '',
    ownCar: false,
    nationality: null,
    gender: null,
  });

  const setPersonalDataDefault = () => {
    setPersonalData({
      id: '',
      userId: '',
      name: '',
      surname: '',
      createdAt: '',
      updatedAt: '',
      ownCar: false,
      nationality: null,
      gender: null,
    });
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // ── Stato foto ──────────────────────────────────────────────────────────────
  const [photoMode, setPhotoMode] = useState<PhotoMode>('url');
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState<string>('');
  const [photoDeleteUrl, setPhotoDeleteUrl] = useState<string>(''); // conservato localmente se vuoi cancellare la vecchia immagine su ImgBB
  const fileInputRef = useRef<HTMLInputElement>(null);
  // ───────────────────────────────────────────────────────────────────────────

  const { locale } = useLocale();
  const { t } = useTranslation();

  useEffect(() => {
    loadPersonalData();
  }, []);

  // Quando il record è caricato, inizializza l'anteprima con l'URL salvato
  useEffect(() => {
    setPhotoPreview(personalData.photoUrl ?? '');
  }, [personalData.photoUrl]);

  const loadPersonalData = async () => {
    try {
      setLoading(true);
      const data = await PersonalDataAPI.Instance<PersonalDataAPI>().getPersonalData();
      setPersonalData(data);
      Log.writeLogDebug('Oggetto caricato');
    } catch (error) {
      if (error instanceof Error && 'response' in error) {
        const httpError = error as any;
        if (httpError.response?.status === 404) {
          setPersonalDataDefault();
        } else {
          setError(t('personalDataPage.errorLoad'));
        }
      } else {
        setError(t('personalDataPage.errorLoad'));
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Gestione URL manuale ────────────────────────────────────────────────────
  const handlePhotoUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    UtilityForm.handleInputChange(e, setPersonalData);
    setPhotoPreview(url);
    setPhotoError('');
  };

  // ── Gestione upload file ────────────────────────────────────────────────────
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validazione lato client
    if (!file.type.startsWith('image/')) {
      setPhotoError('Il file selezionato non è un\'immagine.');
      return;
    }
    if (file.size > 32 * 1024 * 1024) {
      setPhotoError('L\'immagine non può superare 32 MB (limite ImgBB).');
      return;
    }

    // Anteprima locale immediata
    const localPreview = URL.createObjectURL(file);
    setPhotoPreview(localPreview);
    setPhotoError('');

    try {
      setPhotoUploading(true);

      // Opzionale: elimina la vecchia immagine ImgBB prima di caricare la nuova
      // if (photoDeleteUrl) {
      //   await fetch(photoDeleteUrl); // ImgBB supporta DELETE tramite il delete_url
      // }

      const { url, deleteUrl } = await uploadToImgBB(file);

      // Aggiorna lo stato con l'URL definitivo restituito da ImgBB
      setPersonalData((prev) => ({ ...prev, photoUrl: url }));
      setPhotoPreview(url);
      setPhotoDeleteUrl(deleteUrl);
    } catch (err: any) {
      setPhotoError(err.message ?? 'Errore durante l\'upload dell\'immagine.');
      // Ripristina l'anteprima precedente in caso di errore
      setPhotoPreview(personalData.photoUrl ?? '');
    } finally {
      setPhotoUploading(false);
      // Reset input file così si può ricaricare lo stesso file
      if (fileInputRef.current) fileInputRef.current.value = '';
      URL.revokeObjectURL(localPreview);
    }
  };

  const handleRemovePhoto = () => {
    setPersonalData((prev) => ({ ...prev, photoUrl: '' }));
    setPhotoPreview('');
    setPhotoDeleteUrl('');
    setPhotoError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  // ───────────────────────────────────────────────────────────────────────────

  const onSubmit = async () => {
    Log.writeLogDebug('Inizio salvataggio');
    Log.writeLogDebug(personalData);

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const payload = {
        name: personalData.name,
        surname: personalData.surname,
        phone: personalData.phone,
        emailPersonal: personalData.emailPersonal,
        notes: personalData.notes,
        typeLicenseDriver: personalData.typeLicenseDriver,
        birthDate: personalData.birthDate,
        address: personalData.address,
        photoUrl: personalData.photoUrl,
        ownCar: personalData.ownCar ?? false,
        nationality: personalData.nationality ?? null,
        gender: personalData.gender ?? null,
      };

      if (!personalData.id) {
        await PersonalDataAPI.Instance<PersonalDataAPI>().createPersonalData(payload);
        setSuccess(t('personalDataPage.createOk'));
        await loadPersonalData();
      } else {
        await PersonalDataAPI.Instance<PersonalDataAPI>().updatePersonalData(personalData.id, payload);
        setSuccess(t('personalDataPage.updateOk'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('personalDataPage.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Box display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        </Container>
    );
  }

  return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('personalDataPage.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {t('personalDataPage.descriptionPage')}
        </Typography>

        {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
        )}
        {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
        )}

        <Card>
          <CardContent>
            <Box component="form">
              <Grid container spacing={3}>

                {/* ── Nome / Cognome ─────────────────────────────────────────── */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                      fullWidth
                      name="name"
                      label={t('personalDataPage.name')}
                      value={personalData.name}
                      onChange={(e) => UtilityForm.handleInputChange(e, setPersonalData)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                      fullWidth
                      name="surname"
                      label={t('personalDataPage.surname')}
                      value={personalData.surname}
                      onChange={(e) => UtilityForm.handleInputChange(e, setPersonalData)}
                  />
                </Grid>

                {/* ── Data nascita / Email ────────────────────────────────────── */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
                    <DatePicker
                        name="birthDate"
                        label={t('personalDataPage.birthDate')}
                        value={dayjs(personalData.birthDate)}
                        onChange={(date) =>
                            UtilityForm.handleDateChange('birthDate', date, setPersonalData)
                        }
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                      fullWidth
                      name="emailPersonal"
                      label={t('personalDataPage.personalEmail')}
                      type="email"
                      value={personalData.emailPersonal}
                      onChange={(e) => UtilityForm.handleInputChange(e, setPersonalData)}
                  />
                </Grid>

                {/* ── Indirizzo ──────────────────────────────────────────────── */}
                <Grid size={{ xs: 12 }}>
                  <TextField
                      fullWidth
                      name="address"
                      label={t('personalDataPage.address')}
                      multiline
                      rows={2}
                      value={personalData.address}
                      onChange={(e) => UtilityForm.handleInputChange(e, setPersonalData)}
                  />
                </Grid>

                {/* ── Telefono ───────────────────────────────────────────────── */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                      fullWidth
                      name="phone"
                      label={t('personalDataPage.phone')}
                      value={personalData.phone}
                      onChange={(e) => UtilityForm.handleInputChange(e, setPersonalData)}
                  />
                </Grid>

                {/* ── Nazionalità ───────────────────────────────────────── */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <NationalityAutocomplete
                      value={personalData.nationality}
                      onChange={(code) =>
                          setPersonalData((prev) => ({ ...prev, nationality: code }))
                      }
                      lang={locale} // usa la locale corrente ('it' o 'en')
                      label={t('personalDataPage.nationality')}
                  />
                </Grid>

                {/* ── Sesso ─────────────────────────────────────────────── */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                      select
                      fullWidth
                      name="gender"
                      label={t('personalDataPage.gender')}
                      value={personalData.gender ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPersonalData((prev) => ({
                          ...prev,
                          gender: val === '' ? null : (val as 'M' | 'F'),
                        }));
                      }}
                  >
                    <MenuItem value="">
                      <em>{t('standardUse.notSpecified')}</em>
                    </MenuItem>
                    <MenuItem value="M">{t('personalDataPage.genderMale')}</MenuItem>
                    <MenuItem value="F">{t('personalDataPage.genderFemale')}</MenuItem>
                  </TextField>
                </Grid>

                {/* ── SEZIONE FOTO PROFILO ───────────────────────────────────── */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {t('personalDataPage.urlPhoto')}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>

                    {/* Anteprima Avatar */}
                    <Box sx={{ position: 'relative', flexShrink: 0 }}>
                      <Avatar
                          src={photoPreview || undefined}
                          alt="Profile photo"
                          sx={{ width: 100, height: 100, border: '2px solid', borderColor: 'divider' }}
                      >
                        {!photoPreview && <PhotoCameraIcon sx={{ fontSize: 36, color: 'text.disabled' }} />}
                      </Avatar>
                      {photoPreview && (
                          <Tooltip title="Rimuovi immagine">
                            <IconButton
                                size="small"
                                onClick={handleRemovePhoto}
                                sx={{
                                  position: 'absolute',
                                  top: -8,
                                  right: -8,
                                  bgcolor: 'background.paper',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  '&:hover': { bgcolor: 'error.light', color: 'white' },
                                }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                      )}
                    </Box>

                    {/* Tabs URL / Upload */}
                    <Box sx={{ flex: 1, minWidth: 240 }}>
                      <Tabs
                          value={photoMode}
                          onChange={(_, v: PhotoMode) => { setPhotoMode(v); setPhotoError(''); }}
                          sx={{ mb: 1.5 }}
                          variant="fullWidth"
                      >
                        <Tab
                            value="url"
                            label="URL"
                            icon={<LinkIcon fontSize="small" />}
                            iconPosition="start"
                        />
                        <Tab
                            value="upload"
                            label="Upload"
                            icon={<PhotoCameraIcon fontSize="small" />}
                            iconPosition="start"
                        />
                      </Tabs>

                      {photoMode === 'url' && (
                          <TextField
                              fullWidth
                              name="photoUrl"
                              label="URL immagine profilo"
                              placeholder="https://..."
                              value={personalData.photoUrl ?? ''}
                              onChange={handlePhotoUrlChange}
                              size="small"
                          />
                      )}

                      {photoMode === 'upload' && (
                          <Box>
                            {/* Input file nascosto */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleFileSelect}
                            />
                            <Button
                                variant="outlined"
                                startIcon={<PhotoCameraIcon />}
                                onClick={() => fileInputRef.current?.click()}
                                disabled={photoUploading}
                                fullWidth
                            >
                              {photoUploading ? 'Caricamento in corso…' : 'Scegli immagine'}
                            </Button>

                            {photoUploading && (
                                <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />
                            )}

                            {/* URL risultante visibile in sola lettura dopo upload */}
                            {!photoUploading && personalData.photoUrl && (
                                <TextField
                                    fullWidth
                                    label="URL caricato (ImgBB)"
                                    value={personalData.photoUrl}
                                    size="small"
                                    sx={{ mt: 1.5 }}
                                    slotProps={{ input: { readOnly: true } }}
                                />
                            )}

                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Formati supportati: JPG, PNG, GIF, WEBP · Max 32 MB
                            </Typography>
                          </Box>
                      )}

                      {photoError && (
                          <Alert severity="error" sx={{ mt: 1 }}>
                            {photoError}
                          </Alert>
                      )}
                    </Box>
                  </Box>
                </Grid>
                {/* ─────────────────────────────────────────────────────────────── */}

                {/* ── Note ──────────────────────────────────────────────────── */}
                <Grid size={{ xs: 12 }}>
                  <TextField
                      fullWidth
                      name="notes"
                      label={t('personalDataPage.note')}
                      multiline
                      rows={4}
                      value={personalData.notes}
                      onChange={(e) => UtilityForm.handleInputChange(e, setPersonalData)}
                  />
                </Grid>

                {/* ── Patente / Auto propria ─────────────────────────────────── */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                      fullWidth
                      name="typeLicenseDriver"
                      label={t('personalDataPage.licenseDriver')}
                      value={personalData.typeLicenseDriver}
                      onChange={(e) => UtilityForm.handleInputChange(e, setPersonalData)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                      control={
                        <Checkbox
                            name="ownCar"
                            checked={personalData.ownCar}
                            onChange={(e) => UtilityForm.handleInputChange(e, setPersonalData)}
                            color="primary"
                        />
                      }
                      label={t('personalDataPage.ownCar')}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button variant="outlined" disabled={saving}>
                  {t('standardUse.undoBtn')}
                </Button>
                <Button onClick={onSubmit} variant="contained" disabled={saving || photoUploading}>
                  {saving ? <CircularProgress size={24} /> : t('standardUse.saveBtn')}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
  );
};

export default PersonalDataPage;